import { ReactElement, useRef } from "react";

import { Button } from "@/components/_atoms/button";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { cn } from "@/lib/cn";
import { useVirtualizer } from "@tanstack/react-virtual";

const MAX_VISIBLE_YEAR_COUNT = 100;
const ESTIMATED_ITEM_HEIGHT_PX = 88;
const VIRTUAL_ITEM_OVERSCAN = 10;

interface MunicipalPropertyHistoryTimelineProps {
  effectiveYearList: readonly number[];
  selectedYear: number | null;
  isDisabled: boolean;
  onYearChange: (year: number) => void;
}

export function MunicipalPropertyHistoryTimeline({
  effectiveYearList,
  selectedYear,
  isDisabled,
  onYearChange,
}: MunicipalPropertyHistoryTimelineProps): ReactElement {
  const { t } = useDomainTranslation("municipal-property");
  const yearList = [...effectiveYearList].sort((first, second) => second - first);

  const scrollElementRef = useRef<HTMLDivElement>(null);
  const isVirtualized = yearList.length > MAX_VISIBLE_YEAR_COUNT;
  const itemVirtualizer = useVirtualizer<HTMLDivElement, HTMLLIElement>({
    count: isVirtualized ? yearList.length : 0,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => ESTIMATED_ITEM_HEIGHT_PX,
    getItemKey: (index) => yearList[index],
    overscan: VIRTUAL_ITEM_OVERSCAN,
  });
  const virtualItemList = itemVirtualizer.getVirtualItems();
  const visibleYearList = isVirtualized ? virtualItemList.map(({ index }) => yearList[index]) : yearList;

  return (
    <nav aria-label={t("history.timeline")} className="min-w-0 border-b bg-muted/30 p-5 md:border-b-0 md:border-r">
      <h3 className="mb-4 text-sm font-semibold">{t("history.timeline")}</h3>
      <div ref={scrollElementRef} className="max-h-72 overflow-auto">
        <ol className={cn("flex gap-2 md:flex-col", isVirtualized && "flex-col gap-0")}>
          {isVirtualized ? (
            <li role="presentation" aria-hidden="true" style={{ height: virtualItemList[0]?.start ?? 0 }} />
          ) : null}
          {visibleYearList.map((year, index) => (
            <li
              key={year}
              ref={isVirtualized ? itemVirtualizer.measureElement : undefined}
              data-index={isVirtualized ? virtualItemList[index].index : undefined}
              aria-setsize={isVirtualized ? yearList.length : undefined}
              aria-posinset={isVirtualized ? virtualItemList[index].index + 1 : undefined}
              className="relative shrink-0 md:before:absolute md:before:bottom-0 md:before:left-3 md:before:top-8 md:before:w-px md:before:bg-border md:last:before:hidden"
            >
              <Button
                variant="ghost"
                disabled={isDisabled}
                aria-pressed={selectedYear === year}
                onClick={() => onYearChange(year)}
                className={cn(
                  "h-auto w-full justify-start gap-3 whitespace-normal px-2 py-3 text-left",
                  selectedYear === year && "bg-primary/5 text-primary",
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "size-2 shrink-0 rounded-full ring-4 ring-background",
                    selectedYear === year ? "bg-primary" : "bg-muted-foreground/50",
                  )}
                />
                <span className="flex flex-col gap-1">
                  <span className="sr-only">{t("history.standOption", { year })}</span>
                  <span aria-hidden="true" className="text-base font-semibold">
                    {year}
                  </span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {t(year === yearList[0] ? "history.latestStand" : "history.previousStand")}
                  </span>
                </span>
              </Button>
            </li>
          ))}
          {isVirtualized ? (
            <li
              role="presentation"
              aria-hidden="true"
              style={{
                height: itemVirtualizer.getTotalSize() - (virtualItemList.at(-1)?.end ?? 0),
              }}
            />
          ) : null}
        </ol>
      </div>
    </nav>
  );
}
